'use client';

import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';

import cls from 'classnames';

interface ILoadingAnimationProps {
    visible: boolean;
    className?: string;
    fullScreen?: boolean;
    text?: string;
    cardClassName?: string;
    textClassName?: string;
    /** 半透明遮罩，仅 fullScreen 时生效；用于阻断业务页面交互（如支付下单中） */
    mask?: boolean;
}

/** Figma「Group 2037235209」：106×110 卡片、40×40 双色环、loading... 文案 */
function LoadingOverlayCard({
    text,
    cardClassName,
    textClassName,
}: {
    text: string;
    cardClassName?: string;
    textClassName?: string;
}) {
    const rawId = useId();
    const gradId = `loading-spin-grad-${rawId.replace(/:/g, '')}`;

    return (
        <div
            className={cls(
                cardClassName,
                'flex h-[110px] w-[106px] flex-col items-center rounded-[12px] bg-[rgba(0,0,0,0.7)] pb-[16px] pt-[22px]'
            )}
        >
            {/* 加载图标（配色对齐 Figma IMAGE-SVG） */}
            <div className='relative size-[40px] shrink-0'>
                <svg
                    className='size-[40px] shrink-0 animate-spin'
                    style={{ animationDuration: '0.9s' }}
                    viewBox='0 0 40 40'
                    aria-hidden
                >
                    <defs>
                        <linearGradient id={gradId} x1='0%' x2='100%' y1='0%' y2='100%'>
                            <stop offset='0%' stopColor='#AA80FF' />
                            <stop offset='100%' stopColor='#D988FF' />
                        </linearGradient>
                    </defs>
                    <circle cx='20' cy='20' r='17' fill='none' stroke='rgba(255,255,255,0.16)' strokeWidth='3' />
                    <circle
                        cx='20'
                        cy='20'
                        r='17'
                        fill='none'
                        stroke={`url(#${gradId})`}
                        strokeWidth='3'
                        strokeLinecap='round'
                        strokeDasharray='28 80'
                        transform='rotate(-90 20 20)'
                    />
                </svg>
            </div>
            <p
                className={cls(
                    textClassName,
                    'mt-[12px] text-center text-[14px] font-normal leading-[20px] text-white'
                )}
            >
                {text}
            </p>
        </div>
    );
}

export default function LoadingAnimation(props: ILoadingAnimationProps) {
    const {
        visible,
        className = '',
        fullScreen = true,
        text = 'loading...',
        cardClassName,
        textClassName,
        mask = false,
    } = props;

    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!visible) {
        return null;
    }

    const overlay = (
        <div
            className={cls(
                'flex size-full items-center justify-center',
                fullScreen && 'fixed left-0 top-0 z-[999]',
                fullScreen && mask && 'touch-none bg-black/30',
                className
            )}
        >
            <LoadingOverlayCard cardClassName={cardClassName} textClassName={textClassName} text={text} />
        </div>
    );

    if (fullScreen) {
        if (!mounted) {
            return null;
        }
        return createPortal(overlay, document.body);
    }

    return overlay;
}
